import { useState, useEffect } from "react";
import { getAgencies, createAgency, deleteAgency } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Trash2, Building2, Users, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function AgenciesPage() {
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [newAgencyName, setNewAgencyName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchAgencies = async () => {
    try {
      const { data } = await getAgencies();
      setAgencies(data);
    } catch (error) {
      console.error("Failed to fetch agencies:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgencies();
  }, []);

  const handleCreateAgency = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createAgency({ name: newAgencyName });
      setNewAgencyName("");
      setDialogOpen(false);
      fetchAgencies();
    } catch (error) {
      console.error("Failed to create agency:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAgency = async () => {
    if (!selectedAgency) return;
    setSubmitting(true);
    try {
      await deleteAgency(selectedAgency.id);
      setDeleteDialogOpen(false);
      setSelectedAgency(null);
      fetchAgencies();
    } catch (error) {
      console.error("Failed to delete agency:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agencies-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight">Agencies</h1>
          <p className="text-muted-foreground mt-1">Manage your partner agencies</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-agency-button">
              <Plus className="mr-2 h-4 w-4" />
              Add Agency
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Agency</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateAgency} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Agency Name</Label>
                <Input
                  id="name"
                  value={newAgencyName}
                  onChange={(e) => setNewAgencyName(e.target.value)}
                  placeholder="Enter agency name"
                  required
                  data-testid="agency-name-input"
                />
              </div>
              <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-agency-button">
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Agency"
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Agencies List */}
      {agencies.length === 0 ? (
        <Card className="border">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Building2 className="h-16 w-16 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium">No agencies yet</h3>
            <p className="text-muted-foreground text-sm">Create your first agency to get started</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border">
          <CardHeader>
            <CardTitle className="font-outfit">All Agencies ({agencies.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agencies.map((agency) => (
                  <TableRow key={agency.id} className="table-row-hover">
                    <TableCell>
                      <Link
                        to={`/agencies/${agency.id}`}
                        className="font-medium text-primary hover:underline"
                        data-testid={`agency-link-${agency.id}`}
                      >
                        {agency.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        {agency.user_count}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(agency.created_at)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => {
                          setSelectedAgency(agency);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-agency-${agency.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Agency?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{selectedAgency?.name}</strong> and all associated users, clients, and projects. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAgency}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-agency"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
